import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { config } from '../../config/config';

const WIDGET_CONV_KEY = 'topsailWidget:convId:v1';

const getStoredConvId = () => {
  const v = localStorage.getItem(WIDGET_CONV_KEY);
  return v && v !== 'undefined' && v !== 'null' ? v : null;
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

/* ── inline markdown ── */
const renderInline = (s) =>
  String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/`([^`]+)`/g, '<code style="background:#f3f4f6;padding:1px 4px;border-radius:3px;font-size:11px;font-family:monospace">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#2563eb;text-decoration:underline">$1</a>');

const isPipeLine = (l) => /\|/.test(l) && l.trim().startsWith('|');
const isSeparator = (l) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(l);
const splitRow = (l) => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

const renderText = (text) => {
  if (!text) return null;
  const lines = String(text).replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let key = 0;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // fenced code block
    if (/^```/.test(line)) {
      const buf = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) { buf.push(lines[i]); i++; }
      i++;
      out.push(
        <pre key={key++} style={{ background: '#1e293b', color: '#e2e8f0', borderRadius: 8, padding: '10px 12px', overflowX: 'auto', fontSize: 12, margin: '6px 0' }}>
          <code>{buf.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // table
    if (isPipeLine(line) && i + 1 < lines.length && isSeparator(lines[i + 1])) {
      const header = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && isPipeLine(lines[i])) { rows.push(splitRow(lines[i])); i++; }
      out.push(
        <div key={key++} style={{ overflowX: 'auto', margin: '8px 0' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', border: '1px solid #e5e7eb', borderRadius: 6 }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                {header.map((h, idx) => (
                  <th key={idx} style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, borderBottom: '1px solid #e5e7eb', color: '#374151', whiteSpace: 'nowrap' }}
                    dangerouslySetInnerHTML={{ __html: renderInline(h) }} />
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, rIdx) => (
                <tr key={rIdx} style={{ background: rIdx % 2 ? '#f9fafb' : '#fff' }}>
                  {header.map((_, cIdx) => (
                    <td key={cIdx} style={{ padding: '5px 10px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' }}
                      dangerouslySetInnerHTML={{ __html: renderInline(r[cIdx] || '') }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // headings
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const sizes = [16, 14, 13, 13];
      out.push(
        <p key={key++} style={{ fontSize: sizes[level - 1], fontWeight: 700, color: '#111827', margin: '8px 0 2px' }}
          dangerouslySetInnerHTML={{ __html: renderInline(heading[2]) }} />
      );
      i++; continue;
    }

    // unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      out.push(
        <ul key={key++} style={{ paddingLeft: 18, margin: '4px 0', listStyleType: 'disc' }}>
          {items.map((it, idx) => (
            <li key={idx} style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 2 }}
              dangerouslySetInnerHTML={{ __html: renderInline(it) }} />
          ))}
        </ul>
      );
      continue;
    }

    // ordered list
    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      out.push(
        <ol key={key++} style={{ paddingLeft: 18, margin: '4px 0', listStyleType: 'decimal' }}>
          {items.map((it, idx) => (
            <li key={idx} style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 2 }}
              dangerouslySetInnerHTML={{ __html: renderInline(it) }} />
          ))}
        </ol>
      );
      continue;
    }

    if (line.trim() === '') { i++; continue; }

    out.push(
      <p key={key++} style={{ fontSize: 13, lineHeight: 1.6, margin: '2px 0' }}
        dangerouslySetInnerHTML={{ __html: renderInline(line) }} />
    );
    i++;
  }
  return out;
};

export default function AIChatWidget() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const firstName = user?.profile?.firstName || 'there';
  const fullName = [user?.profile?.firstName, user?.profile?.lastName].filter(Boolean).join(' ');
  const [open, setOpen] = useState(false);
  const [bubbleDismissed, setBubbleDismissed] = useState(() => !!sessionStorage.getItem('topsailWidgetBubbleDismissed'));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [convId, setConvId] = useState(() => getStoredConvId());
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  /* ensure a conversation exists */
  useEffect(() => {
    if (!open) return;
    const createConv = async () => {
      try {
        const res = await fetch(`${config.apiUrl}/api/ai/conversations`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ title: 'Quick chat' }),
        });
        if (res.ok) {
          const data = await res.json();
          const id = data.conversation?._id || data._id;
          setConvId(id);
          localStorage.setItem(WIDGET_CONV_KEY, id);
        }
      } catch (_) {}
    };
    const init = async () => {
      const id = convId;
      if (!id) {
        await createConv();
      } else if (messages.length === 0) {
        try {
          const res = await fetch(`${config.apiUrl}/api/ai/conversations/${id}`, { headers: authHeaders() });
          if (res.ok) {
            const data = await res.json();
            const conv = data.conversation || data;
            setMessages((conv.messages || []).filter((m) => m.role !== 'system'));
          } else {
            /* conv deleted – create fresh */
            localStorage.removeItem(WIDGET_CONV_KEY);
            setConvId(null);
            await createConv();
          }
        } catch (_) {}
      }
    };
    init();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /* auto-scroll */
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  /* focus input when opened */
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 80);
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);
    const optimistic = { role: 'user', content: text, _id: `opt-${Date.now()}` };
    setMessages((m) => [...m, optimistic]);
    try {
      let id = convId;
      if (!id) {
        const cr = await fetch(`${config.apiUrl}/api/ai/conversations`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ title: 'Quick chat' }),
        });
        if (cr.ok) {
          const cd = await cr.json();
          id = cd.conversation?._id || cd._id;
          setConvId(id);
          localStorage.setItem(WIDGET_CONV_KEY, id);
        }
      }
      if (!id) { setSending(false); return; }
      const res = await fetch(`${config.apiUrl}/api/ai/chat`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ conversationId: id, message: text }),
      });
      if (res.ok) {
        const data = await res.json();
        const conv = data.conversation || data;
        setMessages((conv.messages || []).filter((m) => m.role !== 'system'));
      }
    } catch (_) {
    } finally {
      setSending(false);
    }
  };

  const handleOpen = () => {
    setOpen((v) => !v);
    if (!bubbleDismissed) {
      setBubbleDismissed(true);
      sessionStorage.setItem('topsailWidgetBubbleDismissed', '1');
    }
  };

  const openFull = () => {
    setOpen(false);
    navigate('/ai');
  };

  return ReactDOM.createPortal(
    <>
      {/* Sticky right-edge tab — desktop only. Slim vertical bar that doesn't hide content */}
      <button
        onClick={handleOpen}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-40 hidden md:flex flex-col items-center gap-2 px-2 py-4 text-white rounded-l-2xl shadow-lg hover:px-3 transition-all duration-200 group"
        title="Topsail GPT — AI Assistant"
        style={{ background: open ? '#1d4ed8' : '#2563eb', writingMode: 'vertical-rl' }}
      >
        <svg
          className="w-5 h-5 transition-transform"
          style={{ writingMode: 'horizontal-tb', transform: open ? 'rotate(180deg)' : undefined }}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
        >
          {open ? (
            <path d="M9 6l6 6-6 6" />
          ) : (
            <>
              <path d="M21 15a4 4 0 01-4 4H8l-5 3V7a4 4 0 014-4h10a4 4 0 014 4v8z" />
              <path d="M9.5 10l.9 2.1L12.5 13l-2.1.9L9.5 16l-.9-2.1L6.5 13l2.1-.9L9.5 10z" fill="currentColor" stroke="none" />
            </>
          )}
        </svg>
        <span
          className="font-semibold text-[11px] tracking-widest uppercase select-none"
          style={{ transform: 'rotate(180deg)' }}
        >
          Topsail GPT
        </span>
      </button>

      {/* First-visit attention badge — small dot on the sticky tab */}
      {!open && !bubbleDismissed && (
        <div
          className="fixed right-1 top-1/2 hidden md:block z-50 pointer-events-none"
          style={{ marginTop: -54 }}
        >
          <span className="block w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white animate-pulse" />
        </div>
      )}

      {/* Backdrop when chat is open */}
      {open && (
        <div
          onClick={handleOpen}
          className="fixed inset-0 z-40 bg-slate-900/20 backdrop-blur-[1px] hidden md:block"
          aria-hidden="true"
        />
      )}

      {/* Chat panel — slides in from the right */}
      <div
        className={`fixed top-0 right-0 z-50 bg-white shadow-2xl border-l border-gray-200 flex flex-col transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        } hidden md:flex`}
        style={{
          width: 'min(440px, calc(100vw - 24px))',
          height: '100vh',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-blue-600 text-white shrink-0">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="8" width="18" height="12" rx="3" />
              <path d="M8 8V6a4 4 0 018 0v2" />
              <circle cx="9" cy="14" r="1.2" fill="currentColor" stroke="none" />
              <circle cx="15" cy="14" r="1.2" fill="currentColor" stroke="none" />
              <path d="M9.5 17.5c.7.7 1.3 1 2.5 1s1.8-.3 2.5-1" />
            </svg>
            <span className="font-semibold text-sm">Topsail GPT</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={openFull}
              className="flex items-center gap-1 text-[11px] text-blue-100 hover:text-white px-2 py-1 rounded hover:bg-blue-500 transition"
              title="Open full chat"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
              </svg>
              Full view
            </button>
            <button
              onClick={handleOpen}
              className="p-1.5 rounded hover:bg-blue-500 transition"
              title="Close"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Greeting */}
        <div className="px-4 py-2 bg-blue-50/40 border-b border-blue-100/40 shrink-0">
          <p className="text-[12px] text-blue-700/80">
            Hey <span className="font-semibold">{fullName || firstName}</span> — how can I help you today?
          </p>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto custom-scrollbar px-4 py-3 space-y-3 bg-gray-50 min-h-0">
          {messages.length === 0 && !sending && (
            <div className="text-center py-8">
              <div className="text-3xl mb-2">✨</div>
              <p className="text-sm font-medium text-gray-700">Hi! I'm Topsail GPT</p>
              <p className="text-[12px] text-gray-500 mt-1">Ask me anything about the CRM.</p>
            </div>
          )}
          {messages.map((m, idx) => (
            <div key={m._id || idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-xl px-3 py-2 text-[13px] ${
                  m.role === 'user'
                    ? 'bg-blue-600 text-white rounded-br-sm'
                    : 'bg-white text-gray-900 border border-gray-200 rounded-bl-sm shadow-sm'
                }`}
              >
                {m.role === 'user'
                  ? <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{m.content}</p>
                  : renderText(m.content)
                }
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-xl rounded-bl-sm px-4 py-3 shadow-sm">
                <div className="flex gap-1.5 items-center h-4">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="px-3 py-3 bg-white border-t border-gray-200 shrink-0">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder="Ask anything…"
              rows={2}
              className="flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={sending}
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              className="p-2.5 rounded-xl bg-blue-600 text-white disabled:opacity-40 hover:bg-blue-700 transition shrink-0"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
